/**
 * ── SURFACING THE SERVER'S OWN ACTIONABLE MESSAGE (E-SF19 P1) ───────────────
 *
 * The BFF answers a refusal with an RFC 7807 `detail` written FOR THE CUSTOMER —
 * "Complete the credit step before signing." — and the storefront threw it away
 * and rendered "We couldn't complete your signing — Something went wrong."
 *
 * Combined with the absent route gating, that left a customer in a permanent dead
 * end: a step they should not have reached, refused for a reason they were never
 * told, with no way to learn what to do. The server knew the answer the whole
 * time.
 *
 * ── WHY THIS IS A WHITELIST OF *STATUSES*, NOT A BLIND PASS-THROUGH ────────
 * Not every `detail` is customer-safe or customer-useful, and rendering upstream
 * text unconditionally is how internal detail reaches an anonymous screen. The
 * BFF is already careful — `problem-details.ts` separates `publicDetail` from
 * `internalDetail` by STRUCTURE and never serialises the latter — but this side
 * must not depend on that being true forever, on every route, including ones not
 * yet written.
 *
 * So:
 *   - 4xx  → the caller is being told how to correct their request. That is the
 *            whole purpose of the field, and it is the case that was broken.
 *   - 5xx  → NEVER. A 500's `detail` is either absent (the BFF sanitises it) or,
 *            on some future route, a leak. The generic message is the honest
 *            thing to say about a failure nobody has explained.
 *   - 429  → excluded even though it is a 4xx: "Too many requests. Try again
 *            later." is worse than the storefront's own rate-limit copy, which can
 *            say what to do and for how long.
 *
 * ⚠️ THIS DOES NOT REPLACE A SPECIFIC BRANCH. Where the SPA recognises a problem
 * TYPE it renders its own copy with an affordance attached (see
 * `quote-draft-error.service.ts` — "try a different meter", "re-enter your ESI
 * ID"). A `detail` is strictly the fallback for a refusal we have no branch for:
 * better than "Something went wrong", worse than a button.
 */

/** Statuses whose `detail` is written for the caller and safe to render. */
function isCustomerSafeStatus(status: number | undefined): boolean {
  if (status === undefined) return false;
  if (status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * The longest `detail` worth rendering inline.
 *
 * A refusal message is a sentence or two. Anything much longer is a stack trace,
 * a validation dump, or an upstream payload that has escaped — none of which
 * belongs on a customer's screen, and all of which would blow the alert's layout.
 * Over the cap we fall back to the generic copy rather than truncate, because a
 * half-sentence of instruction is worse than none.
 */
const MAX_DETAIL_CHARS = 300;

export interface ProblemDetailInput {
  readonly status: number | undefined;
  readonly detail: string | undefined;
}

/**
 * The server's customer-facing explanation, or `null` when the caller should use
 * its own copy.
 *
 * Pure: it takes the two fields it judges, so the API-layer `BffError` type does
 * not leak into `services/` and the rule is testable without a fetch.
 */
export function customerSafeDetail(input: ProblemDetailInput): string | null {
  if (!isCustomerSafeStatus(input.status)) return null;
  const detail = input.detail?.trim();
  if (detail === undefined || detail.length === 0) return null;
  if (detail.length > MAX_DETAIL_CHARS) return null;
  return detail;
}
